import React from 'react';
import LegalPage from './LegalPage';

const PrivacyPolicyPage = ({ onRequestDemo, onRequestQuote }) => {
  const intro = [
    'CTRL Systems, Inc. ("CTRL," "we," "us," or "our") respects your privacy. This Privacy Notice describes how we collect, use, disclose, and protect information collected through <a href="https://www.ctrlsys.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">ctrlsys.com</a> and <a href="https://www.ctrl.aero" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">ctrl.aero</a> (the "Websites") and our related products, services, training, technical support, warranty services, events, and communications (the "Services"). By using the Services you agree to the practices described herein.',
  ];

  const sections = [
    {
      number: '1',
      title: 'Information We Collect',
      blocks: [
        { type: 'sub', number: '1.1', title: 'Information You Provide', blocks: [
          { type: 'p', text: 'We collect information you submit through contact forms, quote/demo requests, resource downloads, event or training registrations, warranty registrations, RMA requests, marketing subscriptions, purchases, and direct correspondence. This may include name, company, job title, email, phone, mailing/billing address, payment information, product ownership details, industry information, technical support requests, and purchase history.' },
        ]},
        { type: 'sub', number: '1.2', title: 'Payment Information', blocks: [
          { type: 'p', text: 'Payments are processed by <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">Stripe</a>. We do not store full payment card data on our servers.' },
        ]},
        { type: 'sub', number: '1.3', title: 'Information Collected Automatically', blocks: [
          { type: 'p', text: 'When you use the Websites we automatically collect IP address, browser type/version, device type, operating system, referring URLs, pages visited, time on page, clickstream activity, approximate geographic location, session identifiers, and advertising interaction data.' },
        ]},
        { type: 'sub', number: '1.4', title: 'Cookies and Tracking', blocks: [
          { type: 'p', text: 'We use cookies, pixels, tags, and similar technologies for functionality, security, analytics, advertising measurement, retargeting, lead attribution, and marketing automation, including Google Analytics, Google Ads, LinkedIn Insight Tag, Meta Pixel, and CRM/automation tools. See our <a href="/legal/cookies" class="text-blue-600 hover:underline">Cookie Policy</a>.' },
        ]},
      ],
    },
    {
      number: '2',
      title: 'Use of Information',
      blocks: [
        { type: 'p', text: 'We use collected information to provide products and services; process payments and fulfill orders; deliver technical support; process warranty claims and RMAs; register users for training or events; deliver requested resources; respond to inquiries; improve our Websites and Services; analyze performance and personalize experiences; send marketing communications; measure and retarget advertising; track campaign attribution; maintain security and prevent fraud; and comply with legal obligations.' },
        { type: 'p', text: 'Processing is based on consent, contract performance, legitimate business interests, and legal compliance.' },
      ],
    },
    {
      number: '3',
      title: 'Disclosure of Information',
      blocks: [
        { type: 'sub', number: '3.1', title: 'Service Providers and Partners', blocks: [
          { type: 'p', text: 'We share personal information with: <strong>Stripe</strong> (payment processing); <a href="https://www.emoryday.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">EmoryDay</a> (marketing, lead generation, advertising optimization, CRM, analytics, retargeting, reporting); <a href="https://www.odoo.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">Odoo</a> (CRM, sales pipelines, orders, warranty logs, RMAs, support records); and <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">Twilio SendGrid</a> / <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">Twilio</a> (email, transactional notifications, marketing, SMS). Each processes information on our behalf under contractual confidentiality and data-protection obligations.' },
        ]},
        { type: 'sub', number: '3.2', title: 'Analytics and Advertising Platforms', blocks: [
          { type: 'p', text: 'Limited information may be shared with advertising and analytics platforms for attribution, performance measurement, and retargeting.' },
        ]},
        { type: 'sub', number: '3.3', title: 'Business Transfers', blocks: [
          { type: 'p', text: 'If CTRL is involved in a merger, acquisition, financing, or sale of assets, personal information may be transferred.' },
        ]},
        { type: 'sub', number: '3.4', title: 'Legal Compliance', blocks: [
          { type: 'p', text: 'We may disclose personal information to comply with legal process, protect rights or property, investigate fraud, enforce agreements, or protect safety. <strong>We do not sell personal information.</strong>' },
        ]},
      ],
    },
    {
      number: '4',
      title: 'Marketing Communications',
      blocks: [
        { type: 'p', text: 'We may send product updates, technical bulletins, case studies, training opportunities, event invitations, and promotional offers. You may unsubscribe at any time using the unsubscribe link or by contacting us. Transactional and service-related communications may still be sent.' },
      ],
    },
    {
      number: '5',
      title: 'Data Retention',
      blocks: [
        { type: 'p', text: 'We retain personal information only as long as necessary for customer support, warranty obligations, business records, legal compliance, accounting and tax obligations, and dispute resolution. Retention periods vary by information type.' },
      ],
    },
    {
      number: '6',
      title: 'Data Security',
      blocks: [
        { type: 'p', text: 'We implement reasonable technical, organizational, and administrative safeguards to protect personal information. No method of transmission or storage is completely secure.' },
      ],
    },
    {
      number: '7',
      title: 'Your Privacy Rights',
      blocks: [
        { type: 'p', text: 'Subject to applicable law, you may have rights to access, correct, delete, restrict processing of, object to processing of, request portability of, or withdraw consent for your personal information, and to opt out of targeted advertising. To exercise these rights, email <a href="mailto:privacy@ctrlsys.com" class="text-blue-600 hover:underline"><strong>privacy@ctrlsys.com</strong></a>.' },
      ],
    },
    {
      number: '8',
      title: 'U.S. State Privacy Rights',
      blocks: [
        { type: 'p', text: 'Residents of California and certain other U.S. states may have additional privacy rights under applicable law. Requests may be submitted to <a href="mailto:privacy@ctrlsys.com" class="text-blue-600 hover:underline">privacy@ctrlsys.com</a>.' },
      ],
    },
    {
      number: '9',
      title: 'International Users',
      blocks: [
        { type: 'p', text: 'Your information may be processed in the United States. By using the Services you consent to this transfer.' },
      ],
    },
    {
      number: '10',
      title: "Children's Privacy",
      blocks: [
        { type: 'p', text: 'The Services are not directed to children under 13. We do not knowingly collect personal information from children.' },
      ],
    },
    {
      number: '11',
      title: 'Third-Party Links and Do Not Track',
      blocks: [
        { type: 'p', text: 'The Websites may contain links to third-party sites. We are not responsible for their privacy practices. The Websites do not currently respond to browser Do Not Track signals.' },
      ],
    },
    {
      number: '12',
      title: 'Changes to this Notice',
      blocks: [
        { type: 'p', text: 'We may update this Privacy Notice periodically. Changes become effective upon posting.' },
      ],
    },
    {
      number: '13',
      title: 'Contact',
      blocks: [
        { type: 'address', lines: [
          '<strong>CTRL Systems, Inc.</strong>',
          '1004 Littlestown Pike, Suite H · Westminster, MD 21157 · United States',
          '<a href="mailto:privacy@ctrlsys.com" class="text-blue-600 hover:underline">privacy@ctrlsys.com</a>',
        ]},
      ],
    },
  ];

  return (
    <LegalPage
      title="Privacy Notice"
      lastUpdated="July 1, 2026"
      intro={intro}
      sections={sections}
      onRequestDemo={onRequestDemo}
      onRequestQuote={onRequestQuote}
    />
  );
};

export default PrivacyPolicyPage;
